import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Clock, XCircle, RotateCcw, HelpCircle } from "lucide-react";
import { getOrderStatusByRef, type OrderStatus } from "@/lib/api/order-status.functions";

export const Route = createFileRoute("/order-success/$ref")({
  head: ({ params }) => ({
    meta: [
      { title: `Order ${params.ref} | Nkadimeng Danny Legal` },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SuccessPage,
});

const STATUS_META: Record<OrderStatus, { label: string; color: string; Icon: any; desc: string }> = {
  pending: { label: "Pending", color: "text-amber-600", Icon: Clock, desc: "We've created your order. Waiting for PayPal to confirm the capture." },
  completed: { label: "Completed", color: "text-emerald-600", Icon: CheckCircle2, desc: "Payment received. We've started preparing your order." },
  refunded: { label: "Refunded", color: "text-blue-600", Icon: RotateCcw, desc: "This payment was refunded. If this wasn't expected, contact us." },
  denied: { label: "Denied", color: "text-red-600", Icon: XCircle, desc: "PayPal denied the payment. No money was taken — please try again or use EFT." },
  unknown: { label: "Awaiting confirmation", color: "text-muted-foreground", Icon: HelpCircle, desc: "We can't find this reference yet. If you just paid, refresh in a few seconds." },
};

function SuccessPage() {
  const { ref } = Route.useParams();
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["order-status", ref],
    queryFn: () => getOrderStatusByRef({ data: { reference: ref } }),
    refetchInterval: (q) => {
      const s = (q.state.data as any)?.status;
      return s === "completed" || s === "refunded" || s === "denied" ? false : 5000;
    },
  });

  const status: OrderStatus = (data?.status as OrderStatus) ?? "unknown";
  const meta = STATUS_META[status];
  const { Icon } = meta;

  return (
    <section className="container-narrow py-12">
      <div className="mx-auto max-w-xl rounded-2xl border border-border bg-card p-8 text-center shadow-soft">
        <Icon className={`mx-auto h-14 w-14 ${meta.color}`} />
        <h1 className="mt-4 font-display text-3xl text-navy">{isLoading ? "Checking status…" : meta.label}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{meta.desc}</p>

        <dl className="mt-6 grid gap-2 text-left text-sm">
          <div className="flex justify-between border-b border-border pb-2">
            <dt className="text-muted-foreground">Reference</dt>
            <dd className="font-mono font-semibold text-navy">{ref}</dd>
          </div>
          {data?.amountZar != null && (
            <div className="flex justify-between border-b border-border pb-2">
              <dt className="text-muted-foreground">Amount</dt>
              <dd className="font-semibold text-navy">R{data.amountZar}{data.amountUsd ? ` (≈ $${data.amountUsd} USD)` : ""}</dd>
            </div>
          )}
          {data?.capturedAt && (
            <div className="flex justify-between border-b border-border pb-2">
              <dt className="text-muted-foreground">Captured</dt>
              <dd className="text-navy">{new Date(data.capturedAt).toLocaleString()}</dd>
            </div>
          )}
          {data?.items && data.items.length > 0 && (
            <div className="border-b border-border pb-2">
              <dt className="text-muted-foreground">Items</dt>
              <dd className="mt-1 text-navy">
                <ul className="list-disc pl-5">
                  {data.items.map((i, idx) => (
                    <li key={idx}>{i.qty}× {i.name}</li>
                  ))}
                </ul>
              </dd>
            </div>
          )}
        </dl>

        {status === "completed" && (
          <p className="mt-6 rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
            A confirmation email has been sent with your receipt and next steps.
          </p>
        )}
        {status === "denied" && (
          <Link to="/checkout" className="mt-6 inline-flex items-center justify-center rounded-md bg-navy px-5 py-2.5 text-sm font-semibold text-navy-foreground">
            Try a different payment method
          </Link>
        )}

        <div className="mt-6 flex flex-wrap justify-center gap-3 text-sm">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="rounded-md border border-input bg-background px-4 py-2 text-navy hover:bg-secondary disabled:opacity-50"
          >
            {isFetching ? "Refreshing…" : "Refresh status"}
          </button>
          <Link to="/contracts" className="rounded-md border border-input bg-background px-4 py-2 text-navy hover:bg-secondary">
            Browse contracts
          </Link>
          <Link to="/" className="rounded-md bg-navy px-4 py-2 font-semibold text-navy-foreground">
            Home
          </Link>
        </div>
      </div>
    </section>
  );
}
